// openEHR RM model — BMM package org.openehr.rm.support.measurement
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import * as openehr_base from "../../base/mod.ts";
import { applyRmInit, type RmInit } from "../../base/apply_rm_init.ts";

/**
 * Defines an object providing proxy access to a measurement information service.
 * Provides validation of units according to UCUM (Unified Code for Units of Measure) specification.
 *
 * This implementation uses a hybrid approach recommended by the openEHR community:
 * - @lhncbc/ucum-lhc library for UCUM validation and conversion (when available)
 * - PropertyUnitData.xml for openEHR property/unit groupings (excluding erroneous conversion factors)
 * - Fallback pattern-based validation when ucum-lhc is not available
 *
 * References:
 * - openEHR Discourse: https://discourse.openehr.org/t/propertyunitdata-xml-and-conversion-information/4968
 * - ucum-lhc: https://github.com/lhncbc/ucum-lhc
 */
export class MEASUREMENT_SERVICE {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * Common valid UCUM unit strings for clinical measurements.
   * This is a subset of the full UCUM specification covering common clinical use cases.
   *
   * NOTE: Both ASCII ('u') and Unicode ('μ') micro prefix variants are included
   * for compatibility. UCUM officially uses ASCII 'u' for micro, but many systems
   * use the Unicode 'μ' character. Both are accepted as valid.
   */
  private static readonly VALID_UNITS = new Set([
    // Mass (includes both ASCII 'ug' and Unicode 'μg' for micro prefix)
    "kg", "g", "mg", "ug", "μg", "ng", "pg",
    "[lb_av]", "[oz_av]",
    // Length (includes both ASCII 'um' and Unicode 'μm' for micro prefix)
    "m", "cm", "mm", "um", "μm", "nm", "km",
    "[in_i]", "[ft_i]", "[mi_i]",
    // Volume (includes both ASCII 'uL' and Unicode 'μL' for micro prefix)
    "L", "l", "dL", "dl", "mL", "ml", "uL", "μL",
    "[gal_us]", "[pt_us]", "[cup_us]", "[tsp_us]", "[tbs_us]",
    // Time
    "s", "min", "h", "d", "wk", "mo", "a",
    // Temperature
    "Cel", "K", "[degF]",
    // Pressure
    "Pa", "kPa", "bar", "mm[Hg]", "[psi]",
    // Concentration (mass) - includes both ASCII and Unicode micro variants
    "mg/dL", "g/dL", "g/L", "mg/L", "ug/L", "μg/L", "ng/L",
    "mg/mL", "ug/mL", "μg/mL",
    // Concentration (substance) - includes both ASCII and Unicode micro variants
    "mol/L", "mmol/L", "umol/L", "μmol/L", "nmol/L",
    "mol/mL", "mmol/mL",
    "mEq/L", "uEq/L",
    // Rate/Flow
    "mL/min", "L/min", "L/h", "mL/h",
    "mL/min/1.73m2", // GFR
    "/min", "/s", "/h",
    // Velocity
    "m/s", "km/h", "[mi_i]/h",
    // Area
    "m2", "cm2", "mm2",
    // BMI
    "kg/m2", "kg/m^2",
    // Heart rate, respiratory rate
    "{beats}/min", "{breaths}/min",
    // Percentages and ratios
    "%", "1", "",
    // pH
    "[pH]",
    // Other clinical
    "[IU]", "[IU]/L", "[IU]/mL",
    "U", "U/L", "U/mL",
    "10*3/uL", "10*6/uL", "10*9/L", "10*12/L",
    "g/dL{RBC}", "pg{Hb}",
    "fL", "pg",
  ]);

  /**
   * UCUM-like patterns for validation when not in known units set.
   *
   * NOTE: The metric prefix character class includes both ASCII 'u' and Unicode 'μ'
   * for micro prefix compatibility. UCUM officially uses ASCII 'u', but many systems
   * use Unicode 'μ'. Both are matched for maximum compatibility.
   */
  private static readonly UCUM_PATTERNS = [
    // Basic unit with optional metric prefix (includes both 'u' and 'μ' for micro)
    /^[yzafpnuμmcdhkMGTPEZY]?[a-zA-Z]+(\d+)?$/,
    // Compound unit with division
    /^[a-zA-Z]+(\d*)\/[a-zA-Z]+(\d*)$/,
    // Units with brackets (special units like mm[Hg])
    /^[a-zA-Z]*\[[^\]]+\]$/,
    // Units with annotations like kg{body}
    /^[a-zA-Z]+\{[^}]+\}$/,
    // Scientific notation units like 10*9/L
    /^10\*\d+\/[a-zA-Z]+$/,
    // Simple ratios with metric prefixes (includes both 'u' and 'μ' for micro)
    /^[yzafpnuμmcdhkMGTPEZY]?[a-zA-Z]+\/[yzafpnuμmcdhkMGTPEZY]?[a-zA-Z]+$/,
  ];

  /**
   * Dimension groups for unit equivalence checking.
   * Units in the same group measure the same physical property.
   */
  private static readonly DIMENSION_GROUPS: Record<string, string[]> = {
    "length": ["m", "cm", "mm", "um", "μm", "nm", "km", "[in_i]", "[ft_i]", "[mi_i]"],
    "mass": ["kg", "g", "mg", "ug", "μg", "ng", "pg", "[lb_av]", "[oz_av]"],
    "volume": ["L", "l", "dL", "dl", "mL", "ml", "uL", "μL", "[gal_us]", "[pt_us]", "[cup_us]"],
    "time": ["s", "min", "h", "d", "wk", "mo", "a"],
    "temperature": ["Cel", "K", "[degF]"],
    "pressure": ["Pa", "kPa", "bar", "mm[Hg]", "[psi]"],
    "concentration_mass": ["mg/dL", "g/dL", "g/L", "mg/L", "ug/L", "μg/L", "ng/L", "mg/mL", "ug/mL", "μg/mL"],
    "concentration_substance": ["mol/L", "mmol/L", "umol/L", "μmol/L", "nmol/L", "mol/mL", "mmol/mL", "mEq/L", "uEq/L"],
    "flow_rate": ["mL/min", "L/min", "L/h", "mL/h"],
    "velocity": ["m/s", "km/h", "[mi_i]/h"],
    "area": ["m2", "cm2", "mm2"],
    "bmi": ["kg/m2", "kg/m^2"],
    "frequency": ["/min", "/s", "/h", "{beats}/min", "{breaths}/min"],
  };

  /**
   * True if the units string 'units' is a valid string according to the HL7 UCUM specification.
   *
   * This method uses pattern-based validation for common UCUM units. For full UCUM
   * validation, use the UcumService from ucum_service.ts which integrates with
   * the @lhncbc/ucum-lhc library.
   *
   * @param units - The units string to validate
   * @returns Result value
   */
  is_valid_units_string(units: openehr_base.String): openehr_base.Boolean {
    const unitsStr = typeof units === 'string' ? units : (units?.value ?? "");

    // Empty string is valid (dimensionless)
    if (unitsStr === "" || unitsStr === "1") {
      return openehr_base.Boolean.from(true);
    }

    // Check against known valid units
    if (MEASUREMENT_SERVICE.VALID_UNITS.has(unitsStr)) {
      return openehr_base.Boolean.from(true);
    }

    // Check against UCUM patterns
    for (const pattern of MEASUREMENT_SERVICE.UCUM_PATTERNS) {
      if (pattern.test(unitsStr)) {
        return openehr_base.Boolean.from(true);
      }
    }

    return openehr_base.Boolean.from(false);
  }

  /**
   * True if two units strings correspond to the same measured property.
   * For example, "m" and "cm" are equivalent (both measure length),
   * but "kg" and "m" are not equivalent (different dimensions).
   *
   * This method uses dimension-based comparison. For more accurate equivalence
   * checking including complex units, use the UcumService from ucum_service.ts
   * which uses actual unit conversion to verify compatibility.
   *
   * @param units1 - First units string
   * @param units2 - Second units string
   * @returns Result value
   */
  units_equivalent(
    units1: openehr_base.String,
    units2: openehr_base.String,
  ): openehr_base.Boolean {
    const u1 = typeof units1 === 'string' ? units1 : (units1?.value ?? "");
    const u2 = typeof units2 === 'string' ? units2 : (units2?.value ?? "");

    // Same units are equivalent
    if (u1 === u2) {
      return openehr_base.Boolean.from(true);
    }

    // Find dimension for each unit
    const getDimension = (unit: string): string | null => {
      for (const [dim, units] of Object.entries(MEASUREMENT_SERVICE.DIMENSION_GROUPS)) {
        if (units.includes(unit)) return dim;
      }
      return null;
    };

    const dim1 = getDimension(u1);
    const dim2 = getDimension(u2);

    // If both have known dimensions and they match, units are equivalent
    if (dim1 && dim2 && dim1 === dim2) {
      return openehr_base.Boolean.from(true);
    }

    return openehr_base.Boolean.from(false);
  }
}
